import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixStoresV1SubscriptionOption from './stores-v1-subscription-option-subscription-options.http.js';

export interface SubscriptionOption {
  /**
   * Subscription option ID (auto-generated upon subscription option creation).
   * @format GUID
   */
  _id?: string | null;
  /**
   * Subscription option title.
   * @minLength 1
   * @maxLength 20
   */
  title?: string | null;
  /**
   * Subscription option description (optional).
   * @maxLength 60
   */
  description?: string | null;
  /** Subscription charge times. For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months. */
  subscriptionSettings?: SubscriptionSettings;
  /**
   * Discount info (optional).
   * For example, a $20 discount would be `value: 20`, `type: AMOUNT`.
   */
  discount?: Discount;
}

export interface SubscriptionSettings {
  /** Frequency of recurring payment. */
  frequency?: SubscriptionFrequencyWithLiterals;
  /** Whether subscription is renewed automatically at the end of each period. */
  autoRenewal?: boolean;
  /**
   * Number of billing cycles before subscription ends. Ignored if `autoRenewal: true`.
   * @min 2
   * @max 999
   */
  billingCycles?: number | null;
}

/** Frequency unit of recurring payment */
export enum SubscriptionFrequency {
  UNDEFINED = 'UNDEFINED',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

/** @enumType */
export type SubscriptionFrequencyWithLiterals =
  | SubscriptionFrequency
  | 'UNDEFINED'
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'YEAR';

export interface Discount {
  /** Discount type. */
  type?: DiscountTypeWithLiterals;
  /** Discount value. */
  value?: number;
}

export enum DiscountType {
  UNDEFINED = 'UNDEFINED',
  /** No discount */
  AMOUNT = 'AMOUNT',
  PERCENT = 'PERCENT',
}

/** @enumType */
export type DiscountTypeWithLiterals =
  | DiscountType
  | 'UNDEFINED'
  | 'AMOUNT'
  | 'PERCENT';

export interface GetSubscriptionOptionRequest {
  /**
   * Subscription option ID.
   * @minLength 1
   * @format GUID
   */
  _id: string;
}

export interface GetSubscriptionOptionResponse {
  /** Subscription option. */
  subscriptionOption?: SubscriptionOption;
}

export interface GetSubscriptionOptionsForProductRequest {
  /**
   * Product ID.
   * @minLength 1
   */
  productId: string;
  /** Whether to include hidden subscription options in the results. */
  includeHiddenSubscriptionOptions?: boolean;
}

export interface GetSubscriptionOptionsForProductResponse {
  /** Subscription options. */
  subscriptionOptions?: SubscriptionOptionInProduct[];
}

export interface SubscriptionOptionInProduct {
  /**
   * Subscription option ID.
   * @format GUID
   */
  _id?: string;
  /** Whether the subscription option is hidden for the product (the default is false). */
  hidden?: boolean;
  /**
   * Subscription option title.
   * @minLength 1
   * @maxLength 20
   * @readonly
   */
  title?: string | null;
  /**
   * Subscription option description (optional).
   * @maxLength 60
   * @readonly
   */
  description?: string | null;
  /**
   * Subscription payment settings. For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months.
   * @readonly
   */
  subscriptionSettings?: SubscriptionSettings;
  /**
   * Discount info (optional).
   * @readonly
   */
  discount?: Discount;
}

export interface ListSubscriptionOptionsRequest {
  /**
   * Subscription option IDs.
   * @format GUID
   * @maxSize 100
   */
  ids?: string[];
}

export interface ListSubscriptionOptionsResponse {
  /** Subscription options. */
  subscriptionOptions?: SubscriptionOption[];
}

export interface CalculatePricesRequest {
  /** original price to which subscription options discount will be applied */
  price?: number;
  /**
   * ids of subscription options which will be applied to original price
   * @format GUID
   * @maxSize 100
   */
  ids?: string[];
}

export interface CalculatePricesResponse {
  /** array of calculated prices */
  prices?: SubscriptionOptionPrices[];
  /** price to which subscription options discount applied */
  originalPrice?: number;
}

export interface SubscriptionOptionPrices {
  /** Subscription option id */
  _id?: string;
  /** Price data calculated for subscription option */
  priceData?: SubscriptionOptionPriceData;
  /** Price data calculated for subscription option, converted to the currency requested in request header */
  convertedPriceData?: SubscriptionOptionPriceData;
}

export interface SubscriptionOptionPriceData {
  /** Subscription option price currency */
  currency?: string;
  /** Price calculated after subscription option discount applied */
  discountedPrice?: number;
  /** Price calculated after subscription option discount applied, formatted with the currency */
  formattedDiscountedPrice?: string;
}

export interface BulkCalculatePricesRequest {
  /**
   * Original prices to which subscription options discount will be applied.
   * Key is identifier unique per price that can be used to match calculated prices with original price
   * @maxSize 1001
   */
  prices?: Record<string, number>;
  /**
   * ids of subscription options which will be applied to original price
   * @format GUID
   * @maxSize 100
   */
  ids?: string[];
}

export interface BulkCalculatePricesResponse {
  /**
   * Key is identifier unique per price that can be used to match calculated prices with original price
   * Value is response with calculated prices for each subscription option
   */
  calculatedPrices?: Record<string, CalculatePricesResponse>;
}

export interface BulkCalculatePricesRequestV2 {
  /**
   * Original prices to be calculated for each corresponding product id with related subscription plan ids
   * @maxSize 100
   */
  items?: BulkCalculatePricesRequestItem[];
}

export interface BulkCalculatePricesRequestItem {
  /**
   * Calculation id (product id)
   * @minLength 1
   * @maxLength 36
   */
  _id?: string;
  /**
   * Original prices to which subscription options discount will be applied.
   * Key is identifier unique per price that can be used to match calculated prices with original price
   * @maxSize 1001
   */
  prices?: Record<string, number>;
  /**
   * ids of subscription options which will be applied to original price.
   * @format GUID
   * @maxSize 100
   */
  subscriptionOptionIds?: string[];
}

export interface BulkCalculatePricesResponseV2 {
  /**
   * Key is identifier unique per price that can be used to match calculated prices with original price
   * Value is response with calculated prices for each subscription option for each product
   */
  calculatedPricesPerProduct?: BulkCalculatePricesResponseItem[];
}

export interface BulkCalculatePricesResponseItem {
  /**
   * Calculation id (product id)
   * @minLength 1
   */
  _id?: string;
  /**
   * Key is identifier unique per price that can be used to match calculated prices with original price
   * Value is response with calculated prices for each subscription option
   */
  calculatedPrices?: Record<string, CalculatePricesResponse>;
}

export interface GetProductIdsForSubscriptionOptionRequest {
  /**
   * Subscription option ID.
   * @minLength 1
   * @format GUID
   */
  _id: string;
  /** Whether to include hidden products in the returned results. */
  includeHiddenProducts?: boolean;
  /** Optional pagination parameters */
  paging?: Paging;
}

export interface Paging {
  /**
   * Amount of items to load per page.
   * @max 100
   */
  limit?: number | null;
  /** Number of items to skip in the display (relevant for all pages after the first). */
  offset?: number | null;
}

export interface GetProductIdsForSubscriptionOptionResponse {
  /** IDs of products associated with the specified subscription option. */
  productIds?: string[];
  /** Paging metadata. */
  metadata?: PagingMetadata;
  /** Number of total results. */
  totalResults?: number;
}

export interface PagingMetadata {
  /** Amount of items to load per page. */
  items?: number;
  /** Number of items to skip in the display (relevant for all pages after the first). */
  offset?: number;
}

export interface GetOneTimePurchasesStatusRequest {
  /**
   * Product ID.
   * @minLength 1
   */
  productId: string;
}

export interface GetOneTimePurchasesStatusResponse {
  /** Whether the specified product is available for one-time purchase */
  allowed?: boolean;
}

export interface CreateSubscriptionOptionRequest {
  /** Subscription option info. */
  subscriptionOption: SubscriptionOption;
}

export interface CreateSubscriptionOptionResponse {
  /** Newly created subscription option. */
  subscriptionOption?: SubscriptionOption;
}

export interface UpdateSubscriptionOptionRequest {
  /** Subscription option info. Only the passed parameters will be updated. */
  subscriptionOption: SubscriptionOption;
}

export interface UpdateSubscriptionOptionResponse {
  /** Updated subscription option. */
  subscriptionOption?: SubscriptionOption;
}

export interface DeleteSubscriptionOptionRequest {
  /**
   * ID of the subscription option to delete.
   * @minLength 1
   * @format GUID
   */
  _id: string;
}

export interface DeleteSubscriptionOptionResponse {}

export interface BulkCreateSubscriptionOptionsRequest {
  /**
   * Subscription options info.
   * @maxSize 100
   */
  subscriptionOptions: SubscriptionOption[];
}

export interface BulkCreateSubscriptionOptionsResponse {
  /** Newly created subscription options. */
  subscriptionOptions?: SubscriptionOption[];
}

export interface BulkUpdateSubscriptionOptionsRequest {
  /**
   * Subscription options info. Only the passed parameters in each subscription option will be updated.
   * @maxSize 100
   */
  subscriptionOptions: SubscriptionOption[];
}

export interface BulkUpdateSubscriptionOptionsResponse {
  /** Updated subscription options. */
  subscriptionOptions?: SubscriptionOption[];
}

export interface BulkDeleteSubscriptionOptionsRequest {
  /**
   * IDs of the subscription options to delete.
   * @maxSize 100
   * @format GUID
   */
  ids: string[];
}

export interface BulkDeleteSubscriptionOptionsResponse {}

export interface AssignSubscriptionOptionsToProductRequest {
  /**
   * Product ID.
   * @minLength 1
   */
  productId: string;
  /**
   * Ordered array of subscription options that will be assigned to the product. Pass an empty array to remove all subscription options from the product.
   * @maxSize 6
   */
  assignedSubscriptionOptions?: SubscriptionOptionInProduct[];
}

export interface AssignSubscriptionOptionsToProductResponse {}

export interface AllowOneTimePurchasesRequest {
  /**
   * Product ID.
   * @minLength 1
   */
  productId: string;
  /** Pass `true` to offer product by subscription and as one-time purchase. Pass `false` to offer product as subscription only. */
  allowed: boolean | null;
}

export interface AllowOneTimePurchasesResponse {}

/**
 * Retrieves a subscription option by ID.
 * @param _id - Subscription option ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @permissionId WIX_STORES.READ_SUBSCRIPTION_OPTIONS
 * @applicableIdentity APP
 * @returns Subscription option.
 * @fqn wix.ecommerce.subscription.option.api.v1.SubscriptionOptionsReadApi.GetSubscriptionOption
 * @deprecated
 */
export async function getSubscriptionOption(
  _id: string
): Promise<
  NonNullablePaths<
    SubscriptionOption,
    | `subscriptionSettings.frequency`
    | `subscriptionSettings.autoRenewal`
    | `discount.type`
    | `discount.value`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ id: _id });

  const reqOpts =
    ambassadorWixStoresV1SubscriptionOption.getSubscriptionOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.subscriptionOption!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves all subscription options assigned to a specified product.
 * By default, hidden subscription options are not returned. To retrieve all subscription options you must pass `includeHiddenSubscriptionOptions = true`.
 * @param productId - Product ID.
 * @public
 * @documentationMaturity preview
 * @requiredField productId
 * @param options - Options.
 * @permissionId WIX_STORES.READ_SUBSCRIPTION_OPTIONS
 * @applicableIdentity APP
 * @fqn wix.ecommerce.subscription.option.api.v1.SubscriptionOptionsReadApi.GetSubscriptionOptionsForProduct
 * @deprecated
 */
export async function getSubscriptionOptionsForProduct(
  productId: string,
  options?: GetSubscriptionOptionsForProductOptions
): Promise<
  NonNullablePaths<
    GetSubscriptionOptionsForProductResponse,
    | `subscriptionOptions`
    | `subscriptionOptions.${number}._id`
    | `subscriptionOptions.${number}.hidden`
    | `subscriptionOptions.${number}.subscriptionSettings.frequency`
    | `subscriptionOptions.${number}.subscriptionSettings.autoRenewal`
    | `subscriptionOptions.${number}.discount.type`
    | `subscriptionOptions.${number}.discount.value`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    productId: productId,
    includeHiddenSubscriptionOptions: options?.includeHiddenSubscriptionOptions,
  });

  const reqOpts =
    ambassadorWixStoresV1SubscriptionOption.getSubscriptionOptionsForProduct(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          productId: '$[0]',
          includeHiddenSubscriptionOptions:
            '$[1].includeHiddenSubscriptionOptions',
        },
        singleArgumentUnchanged: false,
      },
      ['productId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetSubscriptionOptionsForProductOptions {
  /** Whether to include hidden subscription options in the results. */
  includeHiddenSubscriptionOptions?: boolean;
}

/**
 * Retrieves the IDs of products associated with a specified subscription option.
 * @param _id - Subscription option ID.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @param options - Paging and other options.
 * @permissionId WIX_STORES.READ_SUBSCRIPTION_OPTIONS
 * @applicableIdentity APP
 * @fqn wix.ecommerce.subscription.option.api.v1.SubscriptionOptionsReadApi.GetProductIdsForSubscriptionOption
 * @deprecated
 */
export async function getProductIdsForSubscriptionOption(
  _id: string,
  options?: GetProductIdsForSubscriptionOptionOptions
): Promise<
  NonNullablePaths<
    GetProductIdsForSubscriptionOptionResponse,
    `productIds` | `metadata.items` | `metadata.offset` | `totalResults`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    includeHiddenProducts: options?.includeHiddenProducts,
    paging: options?.paging,
  });

  const reqOpts =
    ambassadorWixStoresV1SubscriptionOption.getProductIdsForSubscriptionOption(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          includeHiddenProducts: '$[1].includeHiddenProducts',
          paging: '$[1].paging',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetProductIdsForSubscriptionOptionOptions {
  /** Whether to include hidden products in the returned results. */
  includeHiddenProducts?: boolean;
  /** Optional pagination parameters */
  paging?: Paging;
}

/**
 * Checks whether a specified product (associated with subscription options) is available for one-time purchase.
 * @param productId - Product ID.
 * @public
 * @documentationMaturity preview
 * @requiredField productId
 * @permissionId WIX_STORES.READ_SUBSCRIPTION_OPTIONS
 * @applicableIdentity APP
 * @fqn wix.ecommerce.subscription.option.api.v1.SubscriptionOptionsReadApi.GetOneTimePurchasesStatus
 * @deprecated
 */
export async function getOneTimePurchasesStatus(
  productId: string
): Promise<NonNullablePaths<GetOneTimePurchasesStatusResponse, `allowed`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    productId: productId,
  });

  const reqOpts =
    ambassadorWixStoresV1SubscriptionOption.getOneTimePurchasesStatus(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { productId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['productId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a subscription option.
 * To assign to a product, call [`assignSubscriptionOptionsToProduct()`](https://www.wix.com/velo/reference/wix-stores-v2/subscriptionoptions/assign-subscription-options-to-product).
 * Subscription options that are not assigned to a product will not be visible in the Wix business manager.
 * @param subscriptionOption - Subscription option info.
 * @public
 * @documentationMaturity preview
 * @requiredField subscriptionOption
 * @requiredField subscriptionOption.subscriptionSettings
 * @requiredField subscriptionOption.title
 * @permissionId WIX_STORES.MODIFY_SUBSCRIPTION_OPTIONS
 * @applicableIdentity APP
 * @returns Newly created subscription option.
 * @fqn wix.ecommerce.subscription.option.api.v1.SubscriptionOptionsWriteApi.CreateSubscriptionOption
 * @deprecated
 */
export async function createSubscriptionOption(
  subscriptionOption: NonNullablePaths<
    SubscriptionOption,
    `subscriptionSettings` | `title`,
    2
  >
): Promise<
  NonNullablePaths<
    SubscriptionOption,
    | `subscriptionSettings.frequency`
    | `subscriptionSettings.autoRenewal`
    | `discount.type`
    | `discount.value`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    subscriptionOption: subscriptionOption,
  });

  const reqOpts =
    ambassadorWixStoresV1SubscriptionOption.createSubscriptionOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.subscriptionOption!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { subscriptionOption: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['subscriptionOption']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a subscription option.
 * Only parameters passed will be updated.
 * @param _id - Subscription option ID (auto-generated upon subscription option creation).
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField subscriptionOption
 * @param subscriptionOption - Subscription option update options.
 * @permissionId WIX_STORES.MODIFY_SUBSCRIPTION_OPTIONS
 * @applicableIdentity APP
 * @returns Updated subscription option.
 * @fqn wix.ecommerce.subscription.option.api.v1.SubscriptionOptionsWriteApi.UpdateSubscriptionOption
 * @deprecated
 */
export async function updateSubscriptionOption(
  _id: string,
  subscriptionOption: UpdateSubscriptionOption
): Promise<
  NonNullablePaths<
    SubscriptionOption,
    | `subscriptionSettings.frequency`
    | `subscriptionSettings.autoRenewal`
    | `discount.type`
    | `discount.value`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    subscriptionOption: { ...subscriptionOption, id: _id },
  });

  const reqOpts =
    ambassadorWixStoresV1SubscriptionOption.updateSubscriptionOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.subscriptionOption!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { subscriptionOption: '$[1]' },
        explicitPathsToArguments: { 'subscriptionOption.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'subscriptionOption']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateSubscriptionOption {
  /**
   * Subscription option ID (auto-generated upon subscription option creation).
   * @format GUID
   */
  _id?: string | null;
  /**
   * Subscription option title.
   * @minLength 1
   * @maxLength 20
   */
  title?: string | null;
  /**
   * Subscription option description (optional).
   * @maxLength 60
   */
  description?: string | null;
  /** Subscription charge times. For example, if `frequency: MONTH` and `billingCycles: 6`; payment will be made monthly for 6 months. */
  subscriptionSettings?: SubscriptionSettings;
  /**
   * Discount info (optional).
   * For example, a $20 discount would be `value: 20`, `type: AMOUNT`.
   */
  discount?: Discount;
}

/**
 * Deletes a subscription option.
 * @param _id - ID of the subscription option to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @permissionId WIX_STORES.MODIFY_SUBSCRIPTION_OPTIONS
 * @applicableIdentity APP
 * @fqn wix.ecommerce.subscription.option.api.v1.SubscriptionOptionsWriteApi.DeleteSubscriptionOption
 * @deprecated
 */
export async function deleteSubscriptionOption(_id: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ id: _id });

  const reqOpts =
    ambassadorWixStoresV1SubscriptionOption.deleteSubscriptionOption(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { id: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates multiple subscription options (up to 100).
 * To assign to a product, call [`assignSubscriptionOptionsToProduct()`](https://www.wix.com/velo/reference/wix-stores-v2/subscriptionoptions/assign-subscription-options-to-product).
 * Subscription options that are not assigned to a product will not be visible in the Wix business manager.
 * @param subscriptionOptions - Subscription options info.
 * @public
 * @documentationMaturity preview
 * @requiredField subscriptionOptions
 * @requiredField subscriptionOptions.subscriptionSettings
 * @requiredField subscriptionOptions.title
 * @permissionId WIX_STORES.MODIFY_SUBSCRIPTION_OPTIONS
 * @applicableIdentity APP
 * @fqn wix.ecommerce.subscription.option.api.v1.SubscriptionOptionsWriteApi.BulkCreateSubscriptionOptions
 * @deprecated
 */
export async function bulkCreateSubscriptionOptions(
  subscriptionOptions: NonNullablePaths<
    SubscriptionOption,
    `subscriptionSettings` | `title`,
    2
  >[]
): Promise<
  NonNullablePaths<
    BulkCreateSubscriptionOptionsResponse,
    | `subscriptionOptions`
    | `subscriptionOptions.${number}.subscriptionSettings.frequency`
    | `subscriptionOptions.${number}.subscriptionSettings.autoRenewal`
    | `subscriptionOptions.${number}.discount.type`
    | `subscriptionOptions.${number}.discount.value`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    subscriptionOptions: subscriptionOptions,
  });

  const reqOpts =
    ambassadorWixStoresV1SubscriptionOption.bulkCreateSubscriptionOptions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { subscriptionOptions: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['subscriptionOptions']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates multiple subscription options.
 * Only parameters passed will be updated.
 * @param subscriptionOptions - Subscription options info. Only the passed parameters in each subscription option will be updated.
 * @public
 * @documentationMaturity preview
 * @requiredField subscriptionOptions
 * @requiredField subscriptionOptions._id
 * @permissionId WIX_STORES.MODIFY_SUBSCRIPTION_OPTIONS
 * @applicableIdentity APP
 * @fqn wix.ecommerce.subscription.option.api.v1.SubscriptionOptionsWriteApi.BulkUpdateSubscriptionOptions
 * @deprecated
 */
export async function bulkUpdateSubscriptionOptions(
  subscriptionOptions: NonNullablePaths<SubscriptionOption, `_id`, 2>[]
): Promise<
  NonNullablePaths<
    BulkUpdateSubscriptionOptionsResponse,
    | `subscriptionOptions`
    | `subscriptionOptions.${number}.subscriptionSettings.frequency`
    | `subscriptionOptions.${number}.subscriptionSettings.autoRenewal`
    | `subscriptionOptions.${number}.discount.type`
    | `subscriptionOptions.${number}.discount.value`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    subscriptionOptions: subscriptionOptions,
  });

  const reqOpts =
    ambassadorWixStoresV1SubscriptionOption.bulkUpdateSubscriptionOptions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { subscriptionOptions: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['subscriptionOptions']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Deletes multiple subscription options.
 * @param ids - IDs of the subscription options to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField ids
 * @permissionId WIX_STORES.MODIFY_SUBSCRIPTION_OPTIONS
 * @applicableIdentity APP
 * @fqn wix.ecommerce.subscription.option.api.v1.SubscriptionOptionsWriteApi.BulkDeleteSubscriptionOptions
 * @deprecated
 */
export async function bulkDeleteSubscriptionOptions(
  ids: string[]
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ ids: ids });

  const reqOpts =
    ambassadorWixStoresV1SubscriptionOption.bulkDeleteSubscriptionOptions(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { ids: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['ids']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Assign up to 6 subscription options to a specified product.
 * Pass an empty array to remove all subscription options assigned to a product.
 * @param productId - Product ID.
 * @public
 * @documentationMaturity preview
 * @requiredField productId
 * @param options - Subscription option assignment options.
 * @permissionId WIX_STORES.MODIFY_SUBSCRIPTION_OPTIONS
 * @applicableIdentity APP
 * @fqn wix.ecommerce.subscription.option.api.v1.SubscriptionOptionsWriteApi.AssignSubscriptionOptionsToProduct
 * @deprecated
 */
export async function assignSubscriptionOptionsToProduct(
  productId: string,
  options?: AssignSubscriptionOptionsToProductOptions
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    productId: productId,
    assignedSubscriptionOptions: options?.assignedSubscriptionOptions,
  });

  const reqOpts =
    ambassadorWixStoresV1SubscriptionOption.assignSubscriptionOptionsToProduct(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          productId: '$[0]',
          assignedSubscriptionOptions: '$[1].assignedSubscriptionOptions',
        },
        singleArgumentUnchanged: false,
      },
      ['productId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AssignSubscriptionOptionsToProductOptions {
  /**
   * Ordered array of subscription options that will be assigned to the product. Pass an empty array to remove all subscription options from the product.
   * @maxSize 6
   */
  assignedSubscriptionOptions?: SubscriptionOptionInProduct[];
}

/**
 * Allow for one-time purchase of a product.
 * By default, product can be sold only as part of a subscription, not as a one-time purchase.
 * @param productId - Product ID.
 * @param allowed - Pass `true` to offer product by subscription and as one-time purchase. Pass `false` to offer product as subscription only.
 * @public
 * @documentationMaturity preview
 * @requiredField allowed
 * @requiredField productId
 * @permissionId WIX_STORES.MODIFY_SUBSCRIPTION_OPTIONS
 * @applicableIdentity APP
 * @fqn wix.ecommerce.subscription.option.api.v1.SubscriptionOptionsWriteApi.AllowOneTimePurchases
 * @deprecated
 */
export async function allowOneTimePurchases(
  productId: string,
  allowed: boolean
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    productId: productId,
    allowed: allowed,
  });

  const reqOpts =
    ambassadorWixStoresV1SubscriptionOption.allowOneTimePurchases(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { productId: '$[0]', allowed: '$[1]' },
        singleArgumentUnchanged: false,
      },
      ['productId', 'allowed']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
